import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const n0 = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DEFAULT_CLIENT = "Inalca S.P.A.";
const uid = () => Math.random().toString(36).slice(2);
const blank = (date) => ({ _uid: uid(), date, code: "", description: "", client: DEFAULT_CLIENT, pieces: 1, weight: "", div: 1, line: "", product_id: null, operators: [], _new: true });
const COLS = ["code", "description", "client", "pieces", "weight"];
// il peso arriva come numero dal server e come testo ("1.234,56") dall'input
const numPeso = (v) => (typeof v === "number" ? v
  : Number(String(v ?? "").replace(/\./g, "").replace(",", ".") || 0));

export default function WeightsGrid() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const canDelete = ["admin", "manager"].includes(user?.role);
  const [day, setDay] = useState(params.get("data") || new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [tot, setTot] = useState(null);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ lines: [], clients: [] });
  const [sug, setSug] = useState({ idx: -1, items: [] });
  const [operators, setOperators] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sort, setSort] = useState({ key: "", dir: 1 });
  const [doppione, setDoppione] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);
  const [widths, setWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pesate-widths") || "null") || {}; } catch { return {}; }
  });
  const drag = useRef(null);
  const cellRefs = useRef({});
  const inFlight = useRef({});
  const savedIds = useRef({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/weights/day", { params: { day } });
      setRows([...r.data.rows.map((x) => ({ ...x, _uid: x.id })), blank(day)]);
      setTot(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [day]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/weights/products", { params: { only_active: true } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/weights/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/weights/operators").then((r) => setOperators(r.data)).catch(() => {});
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const byCode = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[(p.code || "").toUpperCase()] = p; });
    return m;
  }, [products]);

  const setCell = (idx, key, value) => {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[idx], [key]: value, _dirty: true };
      if (key === "code") {
        const p = byCode[String(value).toUpperCase()];
        if (p) { row.product_id = p.id; row.description = p.name; row.line = p.line || row.line; }
      }
      next[idx] = row;
      if (idx === next.length - 1 && (row.code || row.description)) next.push(blank(day));
      return next;
    });
    if (key === "code") {
      const v = String(value).toUpperCase();
      setSug({ idx, items: v ? products.filter((p) => (p.code || "").toUpperCase().startsWith(v)
        || (p.name || "").toUpperCase().includes(v)).slice(0, 8) : [] });
    }
  };

  const pick = (idx, p, nextKey = "client") => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], code: p.code, description: p.name, line: p.line || "", product_id: p.id, _dirty: true };
      if (idx === next.length - 1) next.push(blank(day));
      return next;
    });
    setSug({ idx: -1, items: [] });
    let tries = 0;
    const tryFocus = () => {
      if (focusCell(idx, nextKey) || tries++ > 8) return;
      setTimeout(tryFocus, 60);
    };
    setTimeout(tryFocus, 60);
  };

  const keepFocus = (fn) => {
    const id = document.activeElement?.getAttribute?.("data-testid");
    const pos = document.activeElement?.selectionStart;
    return Promise.resolve(fn()).finally(() => {
      if (!id) return;
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${id}"]`);
        if (el && document.activeElement !== el) {
          el.focus();
          if (pos != null) { try { el.setSelectionRange(pos, pos); } catch { /* input non testuale */ } }
        }
      }, 120);
    });
  };

  const persist = async (idx, rowsOverride, force = false) => {
    const r = (rowsOverride || rows)[idx];
    if (!r || !r._dirty) return;
    if (!r.description && !r.code) return;
    if (!r.description) return toast.error("Sigla non riconosciuta: inserisci una descrizione");
    const weight = numPeso(r.weight);
    const chiave = r._uid || `idx-${idx}`;
    // un solo salvataggio per volta per riga: blur, Invio, Tab e frecce non la duplicano
    if (inFlight.current[chiave]) return inFlight.current[chiave];
    const noto = r.id || savedIds.current[chiave];
    // senza peso la riga non viene salvata: evita di creare pesate vuote a 0
    if (!noto && weight <= 0) return;
    // pesata identica a una già registrata nello stesso giorno: si chiede conferma
    if (!noto && !force) {
      const gemella = (rowsOverride || rows).find((x, i) => i !== idx && x.id
        && String(x.description || "") === String(r.description || "")
        && String(x.client || "") === String(r.client || "")
        && Number(x.pieces || 0) === Number(r.pieces || 0)
        && numPeso(x.weight) === weight);
      if (gemella) { setDoppione({ idx, gemella }); return; }
    }
    const task = (async () => {
      try {
        const body = {
          date: day, product_id: r.product_id || null, code: r.code || "", description: r.description,
          client: r.client || "", pieces: Number(r.pieces || 0), weight,
          div: Number(r.div || 1), line: r.line || "ALTRO", notes: "",
          operators: (r.operators || []).map((o) => o.employee_id || o),
        };
        const res = noto ? await api.put(`/weights/entries/${noto}`, body)
          : await api.post(`/weights/entries${force ? "?force=true" : ""}`, body);
        savedIds.current[chiave] = res.data.id;
        setRows((prev) => {
          const next = [...prev];
          if (!next[idx]) return prev;
          next[idx] = { ...res.data, _uid: prev[idx]._uid || res.data.id, _dirty: false };
          return next;
        });
        const t = await api.get("/weights/day", { params: { day } });
        setTot(t.data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { delete inFlight.current[chiave]; }
    })();
    inFlight.current[chiave] = task;
    return task;
  };



  const startResize = (e, key) => {
    e.preventDefault();
    const th = e.target.closest("th");
    drag.current = { key, x: e.clientX, w: th.offsetWidth };
    const move = (ev) => {
      const w = Math.max(48, drag.current.w + ev.clientX - drag.current.x);
      setWidths((prev) => ({ ...prev, [drag.current.key]: w }));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      setWidths((prev) => { localStorage.setItem("pesate-widths", JSON.stringify(prev)); return prev; });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const toggleSort = (key) => setSort((s2) => ({ key, dir: s2.key === key ? -s2.dir : 1 }));

  const del = async (idx) => {
    const r = rows[idx];
    delete savedIds.current[r._uid || `idx-${idx}`];
    if (!r.id) return setRows(rows.filter((_, i) => i !== idx));
    try { await api.delete(`/weights/entries/${r.id}`); toast.success("Pesata eliminata"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const focusCell = (r, k) => {
    const el = document.querySelector(`[data-testid="grid-${k}-${r}"]`) || cellRefs.current[`${r}-${k}`];
    if (el) { el.focus(); el.select?.(); }
    return !!el;
  };

  const move = (idx, key, dir) => {
    const ci = COLS.indexOf(key);
    const next = ci + dir;
    if (next >= 0 && next < COLS.length) return focusCell(idx, COLS[next]);
    if (dir > 0) {
      persist(idx);
      let t = 0;
      const go = () => { if (focusCell(idx + 1, COLS[0]) || t++ > 8) return; setTimeout(go, 60); };
      setTimeout(go, 60);
    }
    else focusCell(idx - 1, COLS[COLS.length - 1]);
  };

  const onKey = (e, idx, key) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const row = rows[idx] || {};
      if (key === "code") {
        // conferma il testo digitato: se corrisponde a una sigla la usa, altrimenti il primo
        // suggerimento; poi passa direttamente al Cliente
        const typed = String(row.code || "").trim().toUpperCase();
        const exact = byCode[typed];
        const first = sug.idx === idx ? sug.items[0] : null;
        if (exact) { pick(idx, exact); return; }
        if (typed && first) { pick(idx, first); return; }
        setSug({ idx: -1, items: [] });
        persist(idx);
        let t = 0;
        const go = () => { if (focusCell(idx, "client") || t++ > 8) return; setTimeout(go, 60); };
        setTimeout(go, 60);
        return;
      }
      if (key === "client") {
        // completa con il suggerimento cliente evidenziato, poi salva il valore completo
        const typed = String(row.client || "").trim().toUpperCase();
        const match = (meta.clients || []).find((c) => c.toUpperCase() === typed)
          || (typed && (meta.clients || []).find((c) => c.toUpperCase().startsWith(typed)))
          || (typed && (meta.clients || []).find((c) => c.toUpperCase().includes(typed)));
        if (match && match.toUpperCase() !== typed) {
          setCell(idx, "client", match);
          setTimeout(() => { persist(idx); focusCell(idx, "pieces"); }, 90);
          return;
        }
      }
      move(idx, key, e.shiftKey ? -1 : 1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault(); persist(idx); setTimeout(() => focusCell(idx + 1, key), 160);
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); persist(idx); setTimeout(() => focusCell(idx - 1, key), 160);
    } else if (e.key === "ArrowRight" && e.ctrlKey) {
      e.preventDefault(); move(idx, key, 1);
    } else if (e.key === "ArrowLeft" && e.ctrlKey) {
      e.preventDefault(); move(idx, key, -1);
    } else if (e.key === "Escape") {
      setSug({ idx: -1, items: [] });
    }
  };

  const ordered = (() => {
    const idxRows = rows.map((r, idx) => ({ r, idx }));
    if (!sort.key) return idxRows;
    const saved = idxRows.filter(({ r }) => r.id);
    const news = idxRows.filter(({ r }) => !r.id);
    const val = (r) => {
      const v = r[sort.key];
      return ["pieces", "weight", "div"].includes(sort.key) ? Number(v || 0) : String(v || "").toUpperCase();
    };
    saved.sort((a, b) => (val(a.r) > val(b.r) ? 1 : val(a.r) < val(b.r) ? -1 : 0) * sort.dir);
    return [...saved, ...news];
  })();

  const cell = (idx, key, extra = "") => (
    <td className={`p-0 border-r border-border relative ${extra}`}>
      <input
        ref={(el) => { cellRefs.current[`${idx}-${key}`] = el; }}
        value={key === "weight" ? String(rows[idx][key] ?? "").replace(".", ",") : (rows[idx][key] ?? "")}
        onChange={(e) => setCell(idx, key,
          key === "code" ? e.target.value.toUpperCase()
            : key === "weight" ? e.target.value.replace(/\./g, ",").replace(/[^\d,-]/g, "")
              : e.target.value)}
        onBlur={() => { setTimeout(() => setSug((s) => (s.idx === idx && key === "code" ? { idx: -1, items: [] } : s)), 150); keepFocus(() => persist(idx)); }}
        onKeyDown={(e) => onKey(e, idx, key)}
        data-testid={`grid-${key}-${idx}`}
        tabIndex={idx * 10 + COLS.indexOf(key) + 1}
        className={`w-full h-8 px-2 bg-transparent outline-none focus:bg-primary/10 focus:ring-1 focus:ring-primary text-sm
          ${["pieces", "weight", "div"].includes(key) ? "text-right tabular-nums" : ""}
          ${key === "code" ? "font-semibold uppercase" : ""}`}
        list={key === "client" ? "clients-list" : key === "line" ? "lines-list" : undefined}
      />
      {key === "code" && sug.idx === idx && sug.items.length > 0 && (
        <div className="absolute left-0 top-8 z-20 w-72 rounded-sm border border-border bg-card shadow-lg"
          data-testid={`grid-suggestions-${idx}`}>
          {sug.items.map((p) => (
            <button key={p.id} onMouseDown={(e) => { e.preventDefault(); pick(idx, p); }}
              data-testid={`grid-suggestion-${p.code}`}
              className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-secondary flex gap-2">
              <span className="font-semibold w-14 shrink-0">{p.code}</span>
              <span className="truncate">{p.name}</span>
              <span className="ml-auto text-muted-foreground shrink-0">{p.line}</span>
            </button>
          ))}
        </div>
      )}
    </td>
  );

  return (
    <div className="space-y-3" data-testid="weights-entries-page">
      <datalist id="clients-list">{(meta.clients || []).map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="lines-list">{(meta.lines || []).map((l) => <option key={l} value={l} />)}</datalist>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Pesi Lavorazione</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solo i dati della pesata: sigla, descrizione, cliente, pezzi e peso. La divisione tra i cottimisti si gestisce in Divisione Pesi.
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
            className="h-9 w-[160px] rounded-sm" data-testid="weights-day" />
        </div>
      </div>

      {tot && (
        <div className="rounded-sm border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm tbl-sticky" data-testid="weights-line-totals">
            <thead className="bg-secondary"><tr>
              {["Linea", "Righe", "Pezzi", "Peso", "%"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {tot.lines.map((l) => (
                <tr key={l.line} className="border-t border-border" data-testid={`line-total-${l.line}`}>
                  <td className="px-3 py-1.5 font-semibold">{l.line}</td>
                  <td className="px-3 py-1.5 tabular-nums">{l.count}</td>
                  <td className="px-3 py-1.5 tabular-nums">{n0(l.pieces)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{n2(l.weight)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{l.percent}%</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-secondary/50 font-semibold">
                <td className="px-3 py-1.5">TOTALE TUTTE LE LINEE</td>
                <td className="px-3 py-1.5 tabular-nums">{tot.count}</td>
                <td className="px-3 py-1.5 tabular-nums">{n0(tot.pieces)}</td>
                <td className="px-3 py-1.5 tabular-nums">{n2(tot.weight)}</td>
                <td className="px-3 py-1.5">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full border-collapse tbl-sticky" data-testid="weights-grid">
          <thead className="bg-secondary sticky top-0">
            <tr>
              {[["code", "Sigla", 96], ["description", "Descrizione lavorazione", 260], ["client", "Cliente", 190],
                ["pieces", "Pezzi", 80], ["weight", "Peso", 96], ["line", "Linea", 128]].map(([key, h, dw]) => (
                <th key={key} style={{ width: widths[key] || dw }}
                  className="relative px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-r border-border select-none">
                  <button onClick={() => toggleSort(key)} data-testid={`grid-sort-${key}`}
                    className="hover:text-foreground transition-colors">
                    {h}{sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
                  </button>
                  <span onMouseDown={(e) => startResize(e, key)} data-testid={`grid-resize-${key}`}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" />
                </th>
              ))}
              <th className="px-2 py-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {ordered.map(({ r, idx }) => (
              <tr key={r._uid || r.id || `new-${idx}`} className={`border-t border-border ${r._new && !r.code ? "bg-secondary/20" : ""}`}
                data-testid={`grid-row-${idx}`}>
                {cell(idx, "code")}
                {cell(idx, "description")}
                {cell(idx, "client")}
                {cell(idx, "pieces")}
                {cell(idx, "weight")}
                {/* la linea è assegnata dal sistema in base alla sigla: sola lettura */}
                <td className="px-2 border-r border-border text-sm" data-testid={`grid-line-${idx}`}>
                  {rows[idx].line
                    ? <span className="font-semibold">{rows[idx].line}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-2 text-center">
                  {(r.id || r.code) && canDelete && (
                    <Button type="button" variant="outline" size="sm"
                      className="h-7 rounded-sm text-rose-600 border-rose-200 hover:bg-rose-50 px-2"
                      data-testid={`grid-delete-${idx}`} onClick={() => setDaEliminare({ idx, r })}>
                      <Trash size={14} className="mr-1" /> Elimina
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Scorciatoie: <span className="font-semibold">Invio</span> e <span className="font-semibold">Tab</span> passano alla cella successiva (a fine riga vanno alla riga nuova) ·
        <span className="font-semibold"> ↑ ↓</span> si muovono tra le righe
      </p>

      <AlertDialog open={!!doppione} onOpenChange={(o) => !o && setDoppione(null)}>
        <AlertDialogContent data-testid="duplicate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pesata già registrata oggi</AlertDialogTitle>
            <AlertDialogDescription data-testid="duplicate-confirm-text">
              {doppione && (
                <>
                  Nella giornata del {day.split("-").reverse().join("/")} risulta già registrata
                  una pesata identica: <span className="font-semibold">{doppione.gemella.description}</span>
                  {" · "}{doppione.gemella.client || "senza cliente"}
                  {" · "}{doppione.gemella.pieces} pezzi
                  {" · "}{String(doppione.gemella.weight).replace(".", ",")} kg.
                  Vuoi registrarla comunque come seconda pesata?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="duplicate-confirm"
              onClick={() => {
                const idx = doppione.idx;
                setDoppione(null);
                persist(idx, null, true);
              }}>
              Salva comunque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!daEliminare} onOpenChange={(o) => !o && setDaEliminare(null)}>
        <AlertDialogContent data-testid="delete-row-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la pesata?</AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-row-text">
              {daEliminare && (
                <>
                  Stai eliminando <span className="font-semibold">{daEliminare.r.description || daEliminare.r.code || "la riga selezionata"}</span>
                  {daEliminare.r.client ? ` · ${daEliminare.r.client}` : ""}
                  {` · ${daEliminare.r.pieces || 0} pezzi · ${String(daEliminare.r.weight ?? "").replace(".", ",")} kg`}.
                  L'operazione non è reversibile.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-row-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-row-confirm"
              onClick={() => { const i = daEliminare.idx; setDaEliminare(null); del(i); }}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
